import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  TbArrowLeft,
  TbUser,
  TbMail,
  TbPhone,
  TbMessage,
  TbCalendar,
} from "react-icons/tb";

import { AxiosInstance } from "../../services/Api";
import styles from "./RequestDemoView.module.css";

const InfoField = ({ icon: Icon, label, value }) => (
  <div className={styles.field}>
    <div className={styles.fieldHeader}>
      {Icon && (
        <span className={styles.fieldIcon}>
          <Icon size={16} />
        </span>
      )}
      <span className={styles.fieldLabel}>{label}</span>
    </div>
    <div className={styles.fieldValue}>{value || "—"}</div>
  </div>
);

const RequestContactView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Guard so we only mark as read once per mount ───
  const markedAsReadRef = useRef(false);

  // ─── Mark as read (silent background call) ───
  const markAsRead = useCallback(async () => {
    if (!id || markedAsReadRef.current) return;
    markedAsReadRef.current = true;

    try {
      const response = await AxiosInstance.post("/roll/contact-update", {
        id: Number(id),
        is_active: 1,
        updated_by: "1",
      });
      console.log("✅ Marked contact as read:", response.data);

      // Reflect new status locally
      setContact((prev) => (prev ? { ...prev, isActive: 1 } : prev));
    } catch (err) {
      console.error("⚠️ Failed to mark contact as read:", err);
      markedAsReadRef.current = false; // allow retry if it failed
    }
  }, [id]);

  // ─── Fetch view details ───
  const fetchContactDetails = useCallback(async () => {
    console.log("🟡 fetchContactDetails id:", id);

    if (!id) {
      setError("No contact request ID provided.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await AxiosInstance.post("/roll/contact-view", {
        id: Number(id),
      });
      console.log("🟢 Contact view API response:", response.data);

      if (response.data?.success && response.data.data) {
        const detail =
          response.data.data.contact ||
          response.data.data.detail ||
          response.data.data;
        setContact(detail);

        // ✅ After successful view → call update API to mark as read
        // Only call if currently unread (isActive === 0) to avoid redundant updates
        if (Number(detail?.isActive) === 0) {
          markAsRead();
        }
      } else {
        throw new Error(
          response.data?.message || "Failed to fetch contact details",
        );
      }
    } catch (err) {
      console.error("🔴 Error fetching contact details:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load contact details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [id, markAsRead]);

  useEffect(() => {
    fetchContactDetails();
  }, [fetchContactDetails]);

  const handleBack = () => navigate("/dashboard/masters/contact-request");

  const fullName = contact
    ? [contact.first_name, contact.last_name].filter(Boolean).join(" ")
    : "";

  return (
    <div className={styles.container}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleBack}
        >
          <TbArrowLeft size={18} />
          <span>Back to List</span>
        </button>
        <h1 className={styles.title}>CONTACT REQUEST DETAILS</h1>
      </div>

      {/* ── Body ── */}
      {loading ? (
        <div className={styles.stateBox}>
          <div className={styles.spinner} />
          <p>Loading contact details...</p>
        </div>
      ) : error ? (
        <div className={styles.stateBox}>
          <p className={styles.errorMessage}>{error}</p>
          <button
            type="button"
            onClick={fetchContactDetails}
            className={styles.retryButton}
          >
            Retry
          </button>
        </div>
      ) : contact ? (
        <>
          {/* Profile strip */}
          <div className={styles.profileCard}>
            <div className={styles.avatar}>
              {fullName
                ? fullName
                    .split(" ")
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()
                : "?"}
            </div>
            <div className={styles.profileInfo}>
              <h2 className={styles.profileName}>{fullName || "Unnamed"}</h2>
              <div className={styles.profileMeta}>
                {contact.email && (
                  <span>
                    <TbMail size={14} /> {contact.email}
                  </span>
                )}
                {contact.phone_number && (
                  <span>
                    <TbPhone size={14} /> {contact.phone_number}
                  </span>
                )}
              </div>
            </div>
            {contact.id != null && (
              <div className={styles.idBadge}>#{contact.id}</div>
            )}
          </div>

          {/* Detail grid */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Contact Information</h3>
            <div className="row g-3">
              <div className="col-12 col-md-6 col-lg-4">
                <InfoField
                  icon={TbUser}
                  label="First Name"
                  value={contact.first_name}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <InfoField
                  icon={TbUser}
                  label="Last Name"
                  value={contact.last_name}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <InfoField icon={TbMail} label="Email" value={contact.email} />
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <InfoField
                  icon={TbPhone}
                  label="Phone"
                  value={contact.phone_number}
                />
              </div>

              {contact.created_at && (
                <div className="col-12 col-md-6 col-lg-4">
                  <InfoField
                    icon={TbCalendar}
                    label="Requested On"
                    value={new Date(contact.created_at).toLocaleDateString(
                      "en-GB",
                    )}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Message — full width */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Message</h3>
            <div className="row g-3">
              <div className="col-12">
                <div className={styles.messageBox}>
                  <span className={styles.fieldIcon}>
                    <TbMessage size={16} />
                  </span>
                  <p className={styles.messageText}>
                    {contact.message || "No message provided."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.stateBox}>
          <p>No contact request data found.</p>
        </div>
      )}
    </div>
  );
};

export default RequestContactView;
